import { create } from 'zustand';
import type { Token, CreateToken, UpdateToken } from '@machina/core';
import { createToken, snapToGrid, TOKEN_SIZE_SQUARES } from '@machina/core';
import { supabase, DbToken } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';

/**
 * Token Store
 *
 * Manages all tokens on the map. Handles selection, movement, and state updates.
 * Syncs with Supabase for multiplayer support.
 */

// Convert Token to DB format
function tokenToDb(token: Token, sceneId: string): Omit<DbToken, 'created_at' | 'updated_at'> {
  return {
    id: token.id,
    scene_id: sceneId,
    character_id: token.ownerId || null,
    data: {
      ...token,
      // Convert dates to ISO strings for JSON storage
      createdAt: token.createdAt instanceof Date ? token.createdAt.toISOString() : token.createdAt,
      updatedAt: token.updatedAt instanceof Date ? token.updatedAt.toISOString() : token.updatedAt,
    },
  };
}

// Convert DB format to Token
function dbToToken(db: DbToken): Token {
  const data = db.data as Record<string, unknown>;
  return {
    ...data,
    id: db.id,
    createdAt: new Date(data.createdAt as string),
    updatedAt: new Date(data.updatedAt as string),
  } as Token;
}

interface TokenState {
  // Token data
  tokens: Map<string, Token>;
  selectedIds: Set<string>;

  // Scene tracking for Supabase sync
  currentSceneId: string | null;
  /** Explicit setter — pushed from useSupabaseSync on scene activation */
  setCurrentSceneId: (sceneId: string | null) => void;
  realtimeCleanup: (() => void) | null;
  isLoading: boolean;

  // Grid settings
  gridSize: number;

  // Actions
  addToken: (params: CreateToken) => Token;
  restoreToken: (token: Token) => void;  // For undo - restores with original id
  removeToken: (id: string) => void;
  updateToken: (params: UpdateToken) => void;
  moveToken: (id: string, to: { x: number; y: number }, snap?: boolean) => void;

  // Selection
  selectToken: (id: string, addToSelection?: boolean) => void;
  deselectToken: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  selectTokensInRect: (rect: { x: number; y: number; width: number; height: number }) => void;

  // Queries
  getToken: (id: string) => Token | undefined;
  getTokenByCombatantId: (combatantId: string) => Token | undefined;
  getSelectedTokens: () => Token[];
  getTokensAtPosition: (position: { x: number; y: number }) => Token[];
  getTokensByOwner: (ownerId: string) => Token[];

  // Combat movement
  moveTowardsTarget: (attackerId: string, targetId: string) => void;

  // Bulk operations
  removeSelected: () => void;
  moveSelected: (delta: { x: number; y: number }) => void;

  // Grid
  setGridSize: (size: number) => void;

  // Sync with combat
  syncFromCombatant: (combatantId: string, data: Partial<Token>) => void;

  // Supabase sync
  loadFromSupabase: (sceneId: string) => Promise<void>;
  subscribeToRealtime: (sceneId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Campaign isolation — wipes all in-memory tokens before a campaign switch
  // so the previous campaign's tokens aren't visible during the load gap.
  clearLocalState: () => void;
}

export const useTokenStore = create<TokenState>((set, get) => ({
  tokens: new Map(),
  selectedIds: new Set(),
  currentSceneId: null,
  setCurrentSceneId: (sceneId) => {
    const prev = get().currentSceneId;
    if (prev !== sceneId) {
      console.log(`[tokenStore] currentSceneId: ${prev ?? 'null'} → ${sceneId ?? 'null'}`);
      set({ currentSceneId: sceneId });
    }
  },
  realtimeCleanup: null,
  isLoading: false,
  gridSize: 50,

  // ==========================================================================
  // TOKEN CRUD
  // ==========================================================================

  addToken: (params) => {
    const token = createToken(params);

    // Snap to grid
    token.position = snapToGrid(token.position, get().gridSize);

    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.set(token.id, token);
      return { tokens: newTokens };
    });

    // Sync to Supabase
    const { currentSceneId } = get();
    if (currentSceneId) {
      console.log('[Token] Syncing new token to Supabase:', token.name, 'scene:', currentSceneId);
      syncTokenToSupabase(token, currentSceneId);
    } else {
      console.warn('[Token] No currentSceneId - token NOT synced to Supabase:', token.name);
    }

    return token;
  },

  restoreToken: (token) => {
    const updatedToken = { ...token, updatedAt: new Date() };
    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.set(token.id, updatedToken);
      return { tokens: newTokens };
    });

    // Sync to Supabase
    const { currentSceneId } = get();
    if (currentSceneId) {
      syncTokenToSupabase(updatedToken, currentSceneId);
    }
  },

  removeToken: (id) => {
    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.delete(id);

      const newSelected = new Set(state.selectedIds);
      newSelected.delete(id);

      return { tokens: newTokens, selectedIds: newSelected };
    });

    // Sync to Supabase
    deleteTokenFromSupabase(id);
  },

  updateToken: (params) => {
    let updatedToken: Token | undefined;

    set((state) => {
      const token = state.tokens.get(params.id);
      if (!token) return state;

      updatedToken = {
        ...token,
        ...params,
        visuals: params.visuals
          ? { ...token.visuals, ...params.visuals }
          : token.visuals,
        updatedAt: new Date(),
      };

      const newTokens = new Map(state.tokens);
      newTokens.set(params.id, updatedToken);
      return { tokens: newTokens };
    });

    // Sync to Supabase
    const { currentSceneId } = get();
    if (currentSceneId && updatedToken) {
      syncTokenToSupabase(updatedToken, currentSceneId);
    }
  },

  moveToken: (id, to, snap = true) => {
    const { gridSize, currentSceneId } = get();
    let position = snap ? snapToGrid(to, gridSize) : to;

    // Clamp to canvas bounds — tokens always land within the map
    const maxPx = 200 * gridSize;
    position = {
      x: Math.max(0, Math.min(position.x, maxPx)),
      y: Math.max(0, Math.min(position.y, maxPx)),
    };

    let updatedToken: Token | undefined;

    set((state) => {
      const token = state.tokens.get(id);
      if (!token || token.locked) return state;

      updatedToken = {
        ...token,
        position,
        updatedAt: new Date(),
      };

      const newTokens = new Map(state.tokens);
      newTokens.set(id, updatedToken);
      return { tokens: newTokens };
    });

    // Sync to Supabase (debounced for smooth dragging)
    if (currentSceneId && updatedToken) {
      syncTokenToSupabase(updatedToken, currentSceneId);
    }
  },

  // ==========================================================================
  // SELECTION
  // ==========================================================================

  selectToken: (id, addToSelection = false) => {
    set((state) => {
      const token = state.tokens.get(id);
      if (!token) return state;

      const newSelected = addToSelection
        ? new Set(state.selectedIds)
        : new Set<string>();

      newSelected.add(id);

      // Update token selected state
      const newTokens = new Map(state.tokens);
      for (const [tokenId, t] of newTokens) {
        const shouldBeSelected = newSelected.has(tokenId);
        if (t.selected !== shouldBeSelected) {
          newTokens.set(tokenId, { ...t, selected: shouldBeSelected });
        }
      }

      return { tokens: newTokens, selectedIds: newSelected };
    });
  },

  deselectToken: (id) => {
    set((state) => {
      const newSelected = new Set(state.selectedIds);
      newSelected.delete(id);

      const token = state.tokens.get(id);
      if (token) {
        const newTokens = new Map(state.tokens);
        newTokens.set(id, { ...token, selected: false });
        return { tokens: newTokens, selectedIds: newSelected };
      }

      return { selectedIds: newSelected };
    });
  },

  clearSelection: () => {
    set((state) => {
      const newTokens = new Map(state.tokens);
      for (const [id, token] of newTokens) {
        if (token.selected) {
          newTokens.set(id, { ...token, selected: false });
        }
      }
      return { tokens: newTokens, selectedIds: new Set() };
    });
  },

  selectAll: () => {
    set((state) => {
      const newSelected = new Set<string>();
      const newTokens = new Map(state.tokens);

      for (const [id, token] of newTokens) {
        newSelected.add(id);
        if (!token.selected) {
          newTokens.set(id, { ...token, selected: true });
        }
      }

      return { tokens: newTokens, selectedIds: newSelected };
    });
  },

  selectTokensInRect: (rect) => {
    const { gridSize } = get();

    set((state) => {
      const newSelected = new Set<string>();
      const newTokens = new Map(state.tokens);

      for (const [id, token] of newTokens) {
        const size = TOKEN_SIZE_SQUARES[token.visuals.size] * gridSize;
        const tokenRight = token.position.x + size;
        const tokenBottom = token.position.y + size;

        const inRect =
          token.position.x < rect.x + rect.width &&
          tokenRight > rect.x &&
          token.position.y < rect.y + rect.height &&
          tokenBottom > rect.y;

        if (inRect) {
          newSelected.add(id);
          if (!token.selected) {
            newTokens.set(id, { ...token, selected: true });
          }
        } else if (token.selected) {
          newTokens.set(id, { ...token, selected: false });
        }
      }

      return { tokens: newTokens, selectedIds: newSelected };
    });
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getToken: (id) => get().tokens.get(id),

  getTokenByCombatantId: (combatantId) => {
    const { tokens } = get();
    for (const token of tokens.values()) {
      if (token.combatantId === combatantId) {
        return token;
      }
    }
    return undefined;
  },

  getSelectedTokens: () => {
    const { tokens, selectedIds } = get();
    return Array.from(selectedIds)
      .map((id) => tokens.get(id))
      .filter((t): t is Token => t !== undefined);
  },

  getTokensAtPosition: (position) => {
    const { tokens, gridSize } = get();
    const result: Token[] = [];

    for (const token of tokens.values()) {
      const size = TOKEN_SIZE_SQUARES[token.visuals.size] * gridSize;
      if (
        position.x >= token.position.x &&
        position.x < token.position.x + size &&
        position.y >= token.position.y &&
        position.y < token.position.y + size
      ) {
        result.push(token);
      }
    }

    return result;
  },

  getTokensByOwner: (ownerId) => {
    const { tokens } = get();
    return Array.from(tokens.values()).filter((t) => t.ownerId === ownerId);
  },

  // ==========================================================================
  // BULK OPERATIONS
  // ==========================================================================

  removeSelected: () => {
    const { selectedIds, tokens } = get();
    const mobIdsToRemove: string[] = [];
    // Sync each deletion to Supabase
    for (const id of selectedIds) {
      const token = tokens.get(id);
      if (token) {
        deleteTokenFromSupabase(id);
        if (token.combatantId) {
          mobIdsToRemove.push(token.combatantId);
        }
      }
    }
    set((state) => {
      const newTokens = new Map(state.tokens);
      for (const id of state.selectedIds) {
        newTokens.delete(id);
      }
      return { tokens: newTokens, selectedIds: new Set() };
    });
    // Clean up linked mobs from DB (deferred import to avoid circular dep)
    if (mobIdsToRemove.length > 0) {
      import('./mobStore').then(async ({ useMobStore }) => {
        for (const mobId of mobIdsToRemove) {
          // removeMob is now DB-aware but token is already removed above,
          // so we only need the DB delete + local state cleanup.
          // removeMob handles this — the token removal is a no-op since we
          // already deleted it.
          await useMobStore.getState().removeMob(mobId);
        }
      });
    }
  },

  moveSelected: (delta) => {
    const { gridSize } = get();

    set((state) => {
      const newTokens = new Map(state.tokens);

      for (const id of state.selectedIds) {
        const token = newTokens.get(id);
        if (token && !token.locked) {
          const newPos = snapToGrid(
            {
              x: token.position.x + delta.x,
              y: token.position.y + delta.y,
            },
            gridSize
          );
          newTokens.set(id, { ...token, position: newPos, updatedAt: new Date() });
        }
      }

      return { tokens: newTokens };
    });
  },

  // ==========================================================================
  // COMBAT MOVEMENT
  // ==========================================================================

  moveTowardsTarget: (attackerCombatantId, targetCombatantId) => {
    const { tokens, gridSize } = get();

    // Find attacker and target tokens by combatantId
    let attackerToken: Token | undefined;
    let targetToken: Token | undefined;

    for (const token of tokens.values()) {
      if (token.combatantId === attackerCombatantId) {
        attackerToken = token;
      }
      if (token.combatantId === targetCombatantId) {
        targetToken = token;
      }
      if (attackerToken && targetToken) break;
    }

    if (!attackerToken || !targetToken) return;
    if (attackerToken.locked) return;

    // Calculate direction from attacker to target
    const dx = targetToken.position.x - attackerToken.position.x;
    const dy = targetToken.position.y - attackerToken.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If already adjacent (within 1.5 grid squares), don't move
    if (distance <= gridSize * 1.5) return;

    // Move to position adjacent to target (1 grid square away)
    // Calculate unit vector and position one grid square from target
    const ux = dx / distance;
    const uy = dy / distance;

    const newX = targetToken.position.x - ux * gridSize;
    const newY = targetToken.position.y - uy * gridSize;

    // Snap to grid
    const snappedPos = snapToGrid({ x: newX, y: newY }, gridSize);

    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.set(attackerToken!.id, {
        ...attackerToken!,
        position: snappedPos,
        updatedAt: new Date(),
      });
      return { tokens: newTokens };
    });
  },

  // ==========================================================================
  // GRID
  // ==========================================================================

  setGridSize: (size) => set({ gridSize: size }),

  // ==========================================================================
  // COMBAT SYNC
  // ==========================================================================

  syncFromCombatant: (combatantId, data) => {
    set((state) => {
      const newTokens = new Map(state.tokens);

      for (const [id, token] of newTokens) {
        if (token.combatantId === combatantId) {
          newTokens.set(id, {
            ...token,
            ...data,
            updatedAt: new Date(),
          });
        }
      }

      return { tokens: newTokens };
    });
  },

  // ==========================================================================
  // SUPABASE SYNC
  // ==========================================================================

  loadFromSupabase: async (sceneId) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('machina_tokens')
        .select('*')
        .eq('scene_id', sceneId);

      if (error) throw error;

      if (get().currentSceneId !== sceneId) {
        console.log('[tokenStore] Scene changed during load, discarding results');
        set({ isLoading: false });
        return;
      }

      if (data && data.length > 0) {
        const newTokens = new Map<string, Token>();
        for (const row of data) {
          const token = dbToToken(row as DbToken);
          newTokens.set(token.id, token);
        }
        set({
          tokens: newTokens,
          currentSceneId: sceneId,
          isLoading: false,
        });
      } else {
        set({
          tokens: new Map(),
          currentSceneId: sceneId,
          isLoading: false,
        });
      }
    } catch (err) {
      console.error('Failed to load tokens:', err);
      set({ isLoading: false });
    }
  },

  clearLocalState: () => {
    set({
      tokens: new Map(),
      selectedIds: new Set(),
      currentSceneId: null,
      isLoading: false,
    });
  },

  subscribeToRealtime: (sceneId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `tokens:${sceneId}`,
      table: 'machina_tokens',
      filter: `scene_id=eq.${sceneId}`,
      event: '*',
      onPayload: (payload) => {
        const { tokens } = get();

        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const token = dbToToken(payload.new as unknown as DbToken);
          const existing = tokens.get(token.id);
          if (existing && existing.updatedAt >= token.updatedAt) return;
          const newTokens = new Map(tokens);
          newTokens.set(token.id, token);
          set({ tokens: newTokens });
        } else if (payload.eventType === 'DELETE') {
          const deletedId = (payload.old as { id: string }).id;
          if (tokens.has(deletedId)) {
            const newTokens = new Map(tokens);
            newTokens.delete(deletedId);
            set({ tokens: newTokens });
          }
        }
      },
    });

    set({ realtimeCleanup: cleanup, currentSceneId: sceneId });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));

// =============================================================================
// SYNC HELPERS (call after local mutations to sync to Supabase)
// =============================================================================

/**
 * Save a token to Supabase (upsert)
 */
export async function syncTokenToSupabase(token: Token, sceneId: string): Promise<void> {
  try {
    const dbToken = tokenToDb(token, sceneId);
    const { error } = await supabase
      .from('machina_tokens')
      .upsert(dbToken, { onConflict: 'id' });

    if (error) throw error;
  } catch (err) {
    console.error('Failed to sync token:', err);
  }
}

/**
 * Delete a token from Supabase
 */
export async function deleteTokenFromSupabase(tokenId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('machina_tokens')
      .delete()
      .eq('id', tokenId);

    if (error) throw error;
  } catch (err) {
    console.error('Failed to delete token:', err);
  }
}
